/**
 * Protocolul dintre worker-ul de fizică și firul de randare.
 *
 * Mod principal: SharedArrayBuffer cu SLOTS stări (inel). Worker-ul scrie mereu
 * în slotul următor, apoi publică indexul cu Atomics. Firul de randare citește
 * ultimele două stări publicate și interpolează între ele, fără copiere.
 *
 * Mod de rezervă (fără izolare cross-origin): postMessage cu ArrayBuffer transferabil.
 */

export const SLOTS = 4;

/** Indecșii din bufferul de control (Int32Array). */
export const CTRL = {
  LATEST: 0,
  SEQ: 1,
  READY: 2,
  SIZE: 8
} as const;

export interface SabState {
  control: SharedArrayBuffer;
  times: SharedArrayBuffer;
  positions: SharedArrayBuffer;
  broken: SharedArrayBuffer;
  nodeCount: number;
  beamCount: number;
}

export interface SabViews {
  control: Int32Array;
  times: Float64Array;
  positions: Float32Array;
  broken: Uint8Array;
  floatsPerSlot: number;
}

export function floatsPerSlot(nodeCount: number): number {
  return nodeCount * 3;
}

export function slotOffset(slot: number, nodeCount: number): number {
  return slot * floatsPerSlot(nodeCount);
}

export function nextSlot(latest: number): number {
  return (latest + 1) % SLOTS;
}

export function prevSlot(latest: number): number {
  return (latest + SLOTS - 1) % SLOTS;
}

export function stateBytes(nodeCount: number, beamCount: number): number {
  return CTRL.SIZE * 4 + SLOTS * 8 + SLOTS * floatsPerSlot(nodeCount) * 4 + beamCount;
}

export function createSabState(nodeCount: number, beamCount: number): SabState {
  return {
    control: new SharedArrayBuffer(CTRL.SIZE * 4),
    times: new SharedArrayBuffer(SLOTS * 8),
    positions: new SharedArrayBuffer(SLOTS * floatsPerSlot(nodeCount) * 4),
    broken: new SharedArrayBuffer(Math.max(4, beamCount)),
    nodeCount,
    beamCount
  };
}

export function sabViews(s: SabState): SabViews {
  return {
    control: new Int32Array(s.control),
    times: new Float64Array(s.times),
    positions: new Float32Array(s.positions),
    broken: new Uint8Array(s.broken),
    floatsPerSlot: floatsPerSlot(s.nodeCount)
  };
}

/** Comenzi de la randare spre worker. */
export type ToWorker =
  | { type: 'init' }
  | { type: 'attach'; sab: SabState | null }
  | { type: 'reset'; height: number; tilt: number }
  | { type: 'impulse'; vx: number; vy: number; vz: number }
  | { type: 'pause'; paused: boolean }
  | { type: 'validate'; requestId: number; json: string; brands: string };

/** Mesaje de la worker spre randare. */
export type FromWorker =
  | {
      type: 'ready';
      nodeCount: number;
      beamCount: number;
      beamPairs: Uint32Array;
      simd: boolean;
      simdModule: boolean;
      unstableBeam: number;
      unstableNode: number;
      mode: 'sab' | 'message';
    }
  | { type: 'state'; seq: number; time: number; positions: ArrayBuffer; broken: ArrayBuffer }
  | { type: 'stats'; stats: PhysicsStats }
  | { type: 'validation'; requestId: number; report: string }
  | { type: 'error'; message: string };

export interface PhysicsStats {
  stepsPerSecond: number;
  microsPerStep: number;
  slowMotion: boolean;
  simTime: number;
  wasmBytes: number;
  brokenBeams: number;
}
